import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Phone, ArrowRight } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import ContactForm from "../components/ContactForm";
import { PageHeader } from "./Services";
import { SITE, ZONES, SERVICES } from "../lib/site";
import { getZoneContent } from "../lib/zonesContent";

export function ZonesList() {
  return (
    <div data-testid="zones-page">
      <SeoHead
        title="Zones d'intervention termites Gironde | Bordeaux, Mérignac, Pessac, Arcachon"
        description="Termite Habitat 33 intervient à Bordeaux, Mérignac, Pessac, Talence, Bègles, Le Bouscat, Bruges, Lormont, Floirac, Arcachon, Libourne et toute la Gironde."
        canonical={SITE.url + "/zones"}
        keywords={["termites Gironde", "intervention termites Bordeaux Métropole", "termites Bassin Arcachon"]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Zones", url: SITE.url + "/zones" },
        ]}
      />
      <PageHeader
        eyebrow="Zones d'intervention"
        title="Bordeaux Métropole, Médoc, Libournais, Bassin d'Arcachon."
        intro="Une couverture complète de la Gironde, avec un délai d'intervention moyen de 48 h."
      />
      <section className="container-x py-20 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ZONES.map((z) => (
          <Link
            key={z.slug}
            to={`/zones/${z.slug}`}
            className="group bg-white border border-[#D5CABD] hover:border-[#7A2E39] hover:shadow-md transition-all p-6 rounded-sm"
            data-testid={`zone-card-${z.slug}`}
          >
            <h2 className="font-serif text-2xl text-gray-900 mb-2">Termites {z.name}</h2>
            <p className="text-sm text-gray-600 mb-4">{z.blurb}</p>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A2E39] inline-flex items-center gap-1">
              Détails locaux <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </section>
      <CTABand />
    </div>
  );
}

export function ZoneDetail() {
  const { city } = useParams();
  const zone = ZONES.find((z) => z.slug === city);
  if (!zone) return <Navigate to="/zones" replace />;
  const content = getZoneContent(zone.slug) || {};
  const faqLocale = content.faqLocale || [];

  return (
    <div data-testid={`zone-detail-${zone.slug}`}>
      <SeoHead
        title={`Traitement Termites ${zone.name} (${zone.postal}) — Termite Habitat 33`}
        description={`Expertise et traitement des termites à ${zone.name} (${zone.insee}, ${zone.postal}). Cabinet expert local Termite Habitat 33, intervention sous 48 h, garantie 5 ans.`}
        canonical={`${SITE.url}/zones/${zone.slug}`}
        keywords={[
          `termites ${zone.name}`,
          `traitement termites ${zone.name}`,
          `expertise termites ${zone.name}`,
          `entreprise termites ${zone.name}`,
        ]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Zones", url: SITE.url + "/zones" },
          { name: zone.name, url: `${SITE.url}/zones/${zone.slug}` },
        ]}
        schema={faqLocale.length > 0 ? [
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `${SITE.legalName} — ${zone.name}`,
            url: `${SITE.url}/zones/${zone.slug}`,
            areaServed: { "@type": "City", name: zone.name },
            telephone: SITE.phoneRaw,
            email: SITE.email,
            priceRange: "€€",
            sameAs: [SITE.parentSite, SITE.facebook],
            address: {
              "@type": "PostalAddress",
              streetAddress: SITE.address,
              postalCode: SITE.postalCode,
              addressLocality: SITE.city,
              addressRegion: SITE.region,
              addressCountry: "FR",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: SITE.aggregateRating.value,
              reviewCount: SITE.aggregateRating.count,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqLocale.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ] : {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `${SITE.legalName} — ${zone.name}`,
          url: `${SITE.url}/zones/${zone.slug}`,
          areaServed: { "@type": "City", name: zone.name },
          telephone: SITE.phoneRaw,
          email: SITE.email,
          priceRange: "€€",
          sameAs: [SITE.parentSite, SITE.facebook],
          address: {
            "@type": "PostalAddress",
            streetAddress: SITE.address,
            postalCode: SITE.postalCode,
            addressLocality: SITE.city,
            addressRegion: SITE.region,
            addressCountry: "FR",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: SITE.aggregateRating.value,
            reviewCount: SITE.aggregateRating.count,
          },
        }}
      />
      <section className="bg-[#E8DCC9]/40 border-b border-[#D5CABD]">
        <div className="container-x py-16 lg:py-24">
          <div className="text-xs text-gray-500 mb-6 flex items-center gap-1">
            <Link to="/" className="hover:text-gray-800">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/zones" className="hover:text-gray-800">Zones</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-800">{zone.name}</span>
          </div>
          <div className="eyebrow mb-3">Zone d'intervention · {zone.insee}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-gray-900 leading-[1.05] mb-5">
            Traitement termites à {zone.name}
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl">{zone.blurb}</p>
        </div>
      </section>

      <section className="container-x py-16 grid lg:grid-cols-3 gap-12">
        <article className="lg:col-span-2 prose-article" data-testid="zone-content">
          <p>
            <strong>{zone.name}</strong> ({zone.postal}, INSEE {zone.insee}) fait partie des
            communes girondines classées à risque par arrêté préfectoral. Notre agence
            locale Termite Bordeaux intervient régulièrement sur ce secteur pour des
            expertises d'expertise parasitaire, des traitements curatifs de charpente, des protocoles
            d'injection termites, le traitement de la mérule et l'audit humidité.
          </p>

          {content.intro && (
            <>
              <h2>Pourquoi {zone.name} est exposée aux termites et insectes du bois</h2>
              <p>{content.intro}</p>
            </>
          )}

          {content.history && (
            <>
              <h2>Le bâti de {zone.name} : caractéristiques locales</h2>
              <p>{content.history}</p>
              {content.bati && <p><strong>Types de constructions traitées :</strong> {content.bati}</p>}
            </>
          )}

          {content.risques && (
            <>
              <h2>Quartiers à risque à {zone.name}</h2>
              <p>{content.risques}</p>
              <p>
                <strong>Tous les quartiers couverts :</strong> {zone.neighborhoods.join(", ")}.
              </p>
            </>
          )}

          {content.references && (
            <>
              <h2>Repères locaux</h2>
              <p>{content.references}</p>
            </>
          )}

          <h2>Nos prestations à {zone.name}</h2>
          <ul>
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`}>{s.title}</Link> — {s.short}
              </li>
            ))}
            <li>
              <Link to="/insectes-xylophages">Insectes xylophages</Link> — capricornes,
              vrillettes, lyctus : fiches techniques détaillées.
            </li>
          </ul>

          <h2>Délai d'intervention à {zone.name}</h2>
          <p>
            {content.delai ||
              `Pour les particuliers de ${zone.name}, nous nous engageons à proposer un rendez-vous d'expertise sous 48 h ouvrées. Les chantiers d'envergure (copropriétés, châteaux, chais, bâtiments classés) font l'objet d'un planning concerté avec le syndic ou le maître d'ouvrage.`}
          </p>

          {faqLocale.length > 0 && (
            <>
              <h2>Questions fréquentes à {zone.name}</h2>
              {faqLocale.map((f, i) => (
                <div key={i}>
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              ))}
            </>
          )}

          <h2>Demander une expertise à {zone.name}</h2>
          <p>
            Appelez-nous au {SITE.phone} ou utilisez notre{" "}
            <Link to="/devis">devis gratuit en ligne en 6 questions</Link>. Vous recevrez une
            réponse personnalisée sous 24 h ouvrées avec une estimation tarifaire indicative.
          </p>
          <p>
            Termite Bordeaux est l'agence locale de Bordeaux Métropole opérée par Termite
            Habitat 33. Pour {zone.name} comme pour les 24 autres villes que nous couvrons,
            nous appliquons la même rigueur et la même{" "}
            <Link to="/services/traitement-termites-bordeaux">garantie 5 ans</Link>.
          </p>
        </article>

        <aside className="lg:sticky lg:top-28 self-start space-y-5">
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm shadow-sm">
            <div className="eyebrow mb-2">Urgence termites</div>
            <h3 className="font-serif text-2xl text-gray-900 mb-4">Intervention à {zone.name}</h3>
            <a href={`tel:${SITE.phoneRaw}`} className="btn-primary w-full mb-3" data-testid="zone-sidebar-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link to="/contact" className="btn-ghost w-full">Demander une expertise</Link>
          </div>

          <div className="bg-[#E8DCC9]/40 border border-[#D5CABD] p-6 rounded-sm">
            <div className="eyebrow mb-3">Communes voisines</div>
            <div className="flex flex-wrap gap-2 text-sm">
              {ZONES.filter((z) => z.slug !== zone.slug).slice(0, 8).map((z) => (
                <Link key={z.slug} to={`/zones/${z.slug}`}
                      className="bg-white border border-[#D5CABD] hover:border-[#7A2E39] px-3 py-1.5 rounded-sm">
                  {z.name}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </section>

      <CTABand title={`Une expertise à ${zone.name} ?`} subtitle="Expertise gratuite, devis transparent sous 24 h." />

      <section className="container-x py-16">
        <h2 className="font-serif text-3xl text-gray-900 mb-6">Décrivez votre situation</h2>
        <div className="max-w-3xl">
          <ContactForm defaultSubject={`Demande d'expertise à ${zone.name}`} />
        </div>
      </section>
    </div>
  );
}
